import { Component } from "react";
import * as Sentry from "@sentry/react";

/**
 * Global ErrorBoundary — catches JS crashes anywhere in the React tree so users
 * never see a blank/black page. Shows a friendly message + reload button.
 * Blocco 4: forwards to Sentry when REACT_APP_SENTRY_DSN is configured.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface the error in browser devtools for debugging
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
    // Blocco 4 — forward to Sentry if enabled; the SDK is a no-op otherwise.
    try {
      Sentry.captureException(error, { contexts: { react: { componentStack: info?.componentStack } } });
    } catch { /* never let monitoring block error UX */ }
  }

  handleReload = () => {
    try { window.location.reload(); } catch { /* noop */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const message = this.state.error?.message || "Errore inatteso";
    return (
      <div
        role="alert"
        data-testid="global-error-boundary"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background: "#0A0A0A",
          color: "#FFFFFF",
          fontFamily: "Manrope, -apple-system, sans-serif",
        }}
      >
        <div style={{ maxWidth: 480, textAlign: "center" }}>
          <div
            style={{
              fontFamily: "Instrument Serif, Georgia, serif",
              fontSize: 40,
              lineHeight: 1.1,
              letterSpacing: "-0.01em",
              marginBottom: 8,
              color: "#FFFFFF",
            }}
          >
            Recen<span style={{ color: "#00F2F4", fontStyle: "italic" }}>Up</span>
          </div>
          <div
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.25em",
              color: "#00F2F4",
              marginBottom: 20,
              fontWeight: 600,
            }}
          >
            Qualcosa è andato storto
          </div>
          <p style={{ fontSize: 14, color: "#A3A3A3", marginBottom: 24, lineHeight: 1.55 }}>
            L'app ha incontrato un errore imprevisto. Prova a ricaricare la pagina — se il
            problema persiste, contattaci rispondendo alla email di RecenUp.
          </p>
          <details
            style={{
              fontSize: 11,
              color: "#737373",
              marginBottom: 24,
              fontFamily: "Menlo, Courier, monospace",
              textAlign: "left",
              wordBreak: "break-word",
            }}
          >
            <summary style={{ cursor: "pointer" }}>Dettagli tecnici</summary>
            <div style={{ marginTop: 8 }}>{message}</div>
          </details>
          <button
            type="button"
            onClick={this.handleReload}
            data-testid="error-boundary-reload"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "12px 28px",
              borderRadius: 999,
              background: "#00F2F4",
              color: "#0A0A0A",
              border: "none",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Ricarica la pagina
          </button>
        </div>
      </div>
    );
  }
}
